import Styles from './AIMenu.module.scss'
import { Menu } from 'components/Menu/Menu'
import { Section } from '@react-stately/collections'
import {
  ChangeLength,
  ChangeTone,
  CopyContent,
  ImproveQuality,
  PlagiarismCheck,
  SEOAnalysis,
  TurnInto,
} from 'blaze-ui/icons/36'
import { MenuProps } from 'components/Menu/Types'
import { Key } from 'react'
import { useImproveQuality } from 'hooks/useImproveQuality'
import { observer } from 'mobx-react-lite'
import { AllSelection } from 'prosemirror-state'
import { useAlmanacEditorState } from 'stores/EditorState'
import { useModals } from 'almanac-ui/Modal'
import { ChangeToneModal } from 'components/BlazeCommandLine/modals/ChangeToneModal'
import { ChangeLengthModal } from 'components/BlazeCommandLine/modals/ChangeLengthModal'
import { useArticleMeta } from 'hooks/useArticleMeta'
import { ArticleMeta } from 'types'
import { ItemElement } from '@react-types/shared'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { DOMSerializer } from 'prosemirror-model'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { TurnIntoWizard } from 'components/Wizards/TurnInto/TurnIntoWizard'
import { SourceType } from 'components/Wizards/TurnInto/Source/Types'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { useRootStore } from 'stores/RootStore'
import { Item } from 'components/Menu/MenuItem'
import { ChevronRightSmall } from 'blaze-ui/icons'
import { SEOScoreGauge } from 'components/SEOAnalysis/SEOScoreGauge/SEOScoreGauge'
import { Item as ReactStatelyItem } from '@react-stately/collections'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props extends Pick<MenuProps<any>, 'menuType' | 'onAction'> {}

const AIMenu = observer(
  ({
    onAction,
    article,
    layerId,
    ...props
  }: Props & {
    article: ArticleMeta
    layerId: string
  }) => {
    const editorState = useAlmanacEditorState()
    const { improveQuality } = useImproveQuality({ articleId: article.id, layerId })
    const { openModal } = useModals()
    const { isMobile } = useResponsiveDesign()

    const handleAction = (action: Key) => {
      const selection = editorState?.selection.empty
        ? new AllSelection(editorState.view.state.doc)
        : editorState?.selection

      onAction?.(action)
      switch (action) {
        case 'improveQuality':
          if (!selection) return

          return improveQuality()
        case 'changeTone':
          if (!selection || !editorState) return

          return openModal(ChangeToneModal, {
            articleId: article.id,
            layerId,
            selection: selection.toJSON(),
            almanacEditorState: editorState,
          })
        case 'changeLength':
          if (!selection || !editorState) return

          return openModal(ChangeLengthModal, {
            articleId: article.id,
            layerId,
            selection: selection.toJSON(),
            almanacEditorState: editorState,
          })
        case 'turnInto':
          if (!selection) return
          return openModal(TurnIntoWizard, {
            sourceType: SourceType.File,
            fromDocProps: {
              documentAsMarkdown: editorState?.view.state.doc
                ? markdownSerializer.serialize(editorState?.view.state.doc)
                : undefined,
              selection,
            },
          })

        case 'seoAnalysis':
          trackEventInHeap(HeapEventType.SeoAnalysis, { action: 'seo-analysis' })
          //handle in parent
          return
        case 'plagiarismCheck':
          //handle in parent
          return
        case 'copyContent':
          if (!editorState) return

          const doc = editorState.view.state.doc

          const serializer =
            editorState.view.someProp('clipboardSerializer') || DOMSerializer.fromSchema(editorState.view.state.schema)
          let fragment = serializer.serializeFragment(doc.content)
          let tmp = document.createElement('div')
          tmp.appendChild(fragment)

          const clipboardItem = new ClipboardItem({
            'text/html': new Blob([tmp.innerHTML], { type: 'text/html' }),
            'text/plain': new Blob([tmp.innerHTML], { type: 'text/plain' }),
          })

          return navigator.clipboard.write([clipboardItem]).then(() => {
            showToastMessage('Doc contents copied!')
          })
      }
    }

    return (
      <Menu {...props} onAction={handleAction} aria-label="AI Actions and Tools" className={Styles.menu}>
        <Section title="Improve your content">
          <Item key="improveQuality" textValue="Improve Quality" frontIcon={ImproveQuality} endIcon={ChevronRightSmall}>
            Improve Quality
          </Item>
          <Item key="changeTone" textValue="Change Tone" frontIcon={ChangeTone} endIcon={ChevronRightSmall}>
            Change Tone
          </Item>
          <Item key="changeLength" textValue="Change Length" frontIcon={ChangeLength} endIcon={ChevronRightSmall}>
            Change Length
          </Item>
          <Item key="turnInto" textValue="Turn Into" frontIcon={TurnInto} endIcon={ChevronRightSmall}>
            Turn Into
          </Item>
        </Section>
        <Section title="Prepare to publish">
          {
            [
              !isMobile && (
                <ReactStatelyItem key="seoScoreGauge">
                  <SEOScoreGauge />
                </ReactStatelyItem>
              ),
              <Item key="seoAnalysis" textValue="SEO Analysis" frontIcon={SEOAnalysis} endIcon={ChevronRightSmall}>
                SEO Analysis
              </Item>,
              <Item
                key="plagiarismCheck"
                textValue="Check for Plagiarism"
                frontIcon={PlagiarismCheck}
                endIcon={ChevronRightSmall}
              >
                Check for Plagiarism
              </Item>,
              <Item key="copyContent" textValue="Copy Content" frontIcon={CopyContent} endIcon={ChevronRightSmall}>
                Copy Content
              </Item>,
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    )
  },
)

const AIMenuContainer = observer((props: Props) => {
  const { docStore } = useRootStore()
  const articleMeta = useArticleMeta(docStore?.articleId || null)

  if (!docStore?.layerId || !articleMeta) return null

  return <AIMenu {...props} article={articleMeta} layerId={docStore.layerId} />
})

export { AIMenuContainer as AIMenu }
